import { SerializedStyles, css } from "@emotion/react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { rem } from "polished";
import React, { ReactNode } from "react";
import { useHistory } from "react-router-dom";

import { Box, Flex, H3, P, Spacer, SubTitle } from "../../base";
import Loading from "../Loading";

export const ModalWrapper = styled(Box)`
  z-index: 99;
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ModalBackground = styled(Box)`
  z-index: 1;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.5);

  ${({ onClick }) =>
    onClick &&
    css`
      cursor: pointer;
    `}
`;

const ModalBox = styled(motion.div)`
  z-index: 2;
  width: ${rem(540)};
  max-width: 100%;

  max-height: calc(100vh - 2rem);
  overflow: scroll;

  border-radius: ${rem(24)};
  box-shadow: 0 ${rem(6)} ${rem(24)} 0 rgba(0, 0, 0, 0.2);
  background-color: #ffffff;

  text-align: center;

  @media screen and (max-width: 560px) {
    max-width: 96%;
  }
`;

const ModalInner = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;

  padding: ${rem(50)} ${rem(56)} ${rem(48)};
`;

interface ModalProps {
  title?: string;
  subtitle?: string;
  copy?: string;
  children?: React.ReactNode;
  onBackgroundClickPath?: string;
  noClick?: boolean;

  boxCss?: SerializedStyles;
  innerCss?: SerializedStyles;

  icon?: any;
  iconProps?: any;
}

const renderIconComponent = (icon, props = {} as any) => {
  const newProps = {
    ...props,
    css: [
      css`
        height: ${rem(64)};
      `,
      props.css,
    ],
  };
  let iconComponent;
  if (typeof icon === "string") {
    iconComponent = <img alt="" src={icon} {...newProps} />;
  } else if (typeof icon === "function") {
    iconComponent = icon(newProps);
  } else if (typeof icon === "object") {
    iconComponent = icon;
  }

  return iconComponent;
};

const variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const Modal = ({
  children,
  title,
  subtitle,
  copy,
  boxCss,
  innerCss,
  onBackgroundClickPath = "/",
  noClick = false,
  icon,
  iconProps,
  ...props
}: ModalProps) => {
  const history = useHistory();

  let onBackgroundClick;
  if (!noClick) {
    onBackgroundClick = () => {
      history.push(onBackgroundClickPath);
    };
  }

  return (
    <ModalWrapper>
      <ModalBackground
        animate="visible"
        exit="hidden"
        initial="hidden"
        transition={{
          type: "tween",
          duration: 0.3,
        }}
        variants={variants}
        onClick={onBackgroundClick}
      />
      <ModalBox
        animate={{ scale: 1, opacity: 1 }}
        css={boxCss}
        exit={{ display: "none" }}
        initial={{ scale: 0.94, opacity: 0.4 }}
        {...props}
      >
        <ModalInner css={innerCss}>
          {icon ? (
            <>
              <Flex>{renderIconComponent(icon, iconProps)}</Flex>
              <Spacer size={30} />
            </>
          ) : null}

          {title ? (
            <>
              <Flex column>
                <H3 bold>{title}</H3>
                {subtitle ? (
                  <>
                    <Spacer size={8} />
                    <SubTitle>{subtitle}</SubTitle>
                  </>
                ) : null}
              </Flex>
              <Spacer size={24} />
            </>
          ) : null}
          {copy ? (
            <>
              <P
                css={css`
                  max-width: 36rem;
                  align-self: center;
                  margin: 0;
                `}
              >
                {copy}
              </P>
              <Spacer size={36} />
            </>
          ) : null}
          <React.Suspense fallback={Loading as unknown as ReactNode}>
            {children}
          </React.Suspense>
        </ModalInner>
      </ModalBox>
    </ModalWrapper>
  );
};

export default Modal;
